import { useAnalytics, useTenant } from '@parcel2go/npm-p2g-app';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import useSWR from 'swr';
import { authenticatedFetchWithTenantAnalytics } from '~/lib/fetch';
import { capturePromiseException } from '~/lib/metrics';
import { P2GAlert, P2GBox, P2GFade, P2GLink, P2GText } from '@parcel2go/core-ui';
import { TranslationNamespaces } from '~/lib/constants';
import { SidePanelContent } from '~/components/sidepanel-section';
import { Trans, useTranslation } from 'react-i18next';
import { LinkModel } from './book/LinkModel';
import {
  generateLabelEndpointFromStatusEndpoint,
  generatePresignedLabelMeta,
  generateSwrCacheKeyForLink,
  generateTranslationKey,
  isReady,
  labelTranslationMap,
  StatusData
} from './labels/label-lib';
import OrderIndividualLabels from './OrderIndividualLabels';
import { SettingsCache } from '~/lib/consts/settings-cache';

interface props {
  isParent: boolean;
  onCustomerWantsToSeeLabels?: () => void;
  children?: React.ReactNode;
  setPreSignedLinks?: (links?: LinkModel[]) => void;
  statusParams?: {
    statusEndpoint?: string;
    body?: {
      orderId?: string;
      orderLineId?: string;
      orderLineIds?: string[];
    };
  };
}

const OrderLabelStatusObserver = ({
  onCustomerWantsToSeeLabels,
  setPreSignedLinks,
  statusParams: { statusEndpoint, body: statusParams } = {},
  isParent,
  children
}: props) => {
  const { user } = useAuth();
  const { tenant, settings } = useTenant();
  const { session } = useAnalytics();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const [takingTooLong, setTakingTooLong] = useState(false);
  const [customerWantsToSeeLabels, setCustomerWantsToSeeLabels] = useState(false);
  const [called, setCalled] = useState(false);

  const onCustomerWantsToSeeLabelsClick = () => {
    if (onCustomerWantsToSeeLabels) {
      onCustomerWantsToSeeLabels();
    }
    setCustomerWantsToSeeLabels(true);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setTakingTooLong(true);
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  const { data } = useSWR<StatusData>(
    generateSwrCacheKeyForLink({ Href: statusEndpoint, Meta: statusParams, Type: 'ORDER-LABELS-STATUS' }),
    () =>
      statusEndpoint &&
      authenticatedFetchWithTenantAnalytics(user, tenant, settings, session, statusEndpoint, {
        method: 'POST',
        body: JSON.stringify(statusParams),
        headers: {
          'Content-Type': 'application/json'
        }
      })
        .then((res) => res.json())
        .catch(capturePromiseException),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      revalidateOnMount: false,
      refreshInterval: (d) => (d === undefined ? 1000 : customerWantsToSeeLabels || isReady(d) ? 0 : 2500) // Refresh every second if we dont have a pre-signed URL
    }
  );

  const allReady = useMemo(() => isReady(data) ?? false, [data]);

  const complete = useCallback(() => {
    // Pass back pre-signed links based on the status response.
    if (setPreSignedLinks && data?.result?.labelOptions) {
      const labelsToAdd: LinkModel[] = [];

      data.result.labelOptions.forEach((option) => {
        const k = generateTranslationKey(option);
        const sanitisedType = k.replaceAll('_', '-');

        // Standard Label Options
        labelsToAdd.push({
          Href: generateLabelEndpointFromStatusEndpoint(statusEndpoint),
          Type: `LABELS-${sanitisedType}_PRESIGNED`,
          Label: t(`LINK_${k}`, labelTranslationMap[k]),
          Meta: generatePresignedLabelMeta(
            option,
            false,
            statusParams.orderId,
            statusParams.orderLineId,
            statusParams.orderLineIds?.map(String)
          )
        });

        // Pick and Pack Labels (@TODO Not on mew API yet)
        if (SettingsCache.settings.Features.includes('PackageContents')) {
          // labelsToAdd.push({
          //   Href: generateLabelEndpointFromStatusEndpoint(statusEndpoint),
          //   Type: `LABELS-PACKING-${sanitisedType}_PRESIGNED`,
          //   Label: t(`LINK_${k}`, labelTranslationMap[k]),
          //   Meta: generatePresignedLabelMeta(
          //     option,
          //     true,
          //     statusParams.orderId,
          //     statusParams.orderLineId,
          //     statusParams.orderLineIds?.map(String)
          //   ),
          //   LinkGroup: 'LabelWithPackingSlip'
          // });
        }
      });

      setPreSignedLinks(labelsToAdd);
    }
  }, [setPreSignedLinks, data, t, statusParams, statusEndpoint]);

  useEffect(() => {
    if (allReady && !called) {
      setCalled(true);
      complete();
    }
  }, [allReady, complete, called]);

  const orderLineStatuses = useMemo(() => {
    return data?.result?.orderLineStatuses || [];
  }, [data]);

  if (allReady || customerWantsToSeeLabels || !statusParams) {
    return (
      <P2GFade in>
        <>
          <>{children}</>
          {isParent && (
            <OrderIndividualLabels
              wrapper={(x) => <P2GBox mt={2}>{x}</P2GBox>}
              statuses={orderLineStatuses}
              link={t('ORDER_INDIVIDUAL_LABELS_LINK', 'View Individual Labels')}
            />
          )}
        </>
      </P2GFade>
    );
  }

  return (
    <P2GAlert type="info">
      <SidePanelContent>
        <P2GText variant="body2">{t('CHECKING_LABEL_STATUS', { defaultValue: 'Checking label status...' })}</P2GText>
        {takingTooLong && (
          <>
            {statusParams ? (
              <P2GText variant="body2">
                <Trans
                  t={t}
                  i18nKey="LABEL_STATUS_PRESIGNED_INFO"
                  defaults="Its taking longer than expected to get your labels."
                />
                {isParent && (
                  <OrderIndividualLabels
                    wrapper={(x) => <>{x}</>}
                    statuses={orderLineStatuses}
                    link={t('ORDER_INDIVIDUAL_LABELS_LINK', 'View Individual Labels')}
                  />
                )}
              </P2GText>
            ) : (
              <>
                <P2GText variant="body2">
                  <Trans
                    t={t}
                    i18nKey="LABEL_STATUS_INFO"
                    defaults="Its taking longer than expected to get your labels.<br />You can try to download these now if you'd prefer."
                  />
                </P2GText>
                {onCustomerWantsToSeeLabelsClick && (
                  <P2GText variant="body2">
                    <P2GLink onClick={onCustomerWantsToSeeLabelsClick}>
                      {t('TRY_LABELS_LINK', { defaultValue: 'Try Labels' })}
                    </P2GLink>
                  </P2GText>
                )}
              </>
            )}
          </>
        )}
      </SidePanelContent>
    </P2GAlert>
  );
};

export default OrderLabelStatusObserver;
